import React, { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Section } from './Shared';
type PreviewPage = {
  src: string;
  alt: string;
  label: string;
};
const previewPages: PreviewPage[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/am1Kjq4Cq4B93TDqbQtmxL/Screenshot_20260713-191748~2.png',
  alt: 'Página del ritual breve para encender la presencia en el cuerpo',
  label: 'Rituales de presencia y contención'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/wm757Ncp8Fe5Wvo4yMo1f9/Screenshot_20260713-191814~2.png',
  alt: 'Página sobre sueños, aromas y recuerdos de mamá',
  label: 'Recuerdos que acompañan'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/138Zi88KRCnEg36S9tudpA/Screenshot_20260713-191847~2.png',
  alt: 'Página de ejercicios para trabajar la culpa y el perdón',
  label: 'Ejercicios de escritura sanadora'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/c98Cr4NkhEa3mavo3gsBiE/Screenshot_20260713-191912~2.png',
  alt: 'Plantilla guiada para escribir una carta a mamá',
  label: 'Palabras que necesitaban un lugar'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dJteJg62GsdbfcGwocJKSh/Screenshot_20260713-191934~2.png',
  alt: 'Guía para armar un rincón de recuerdo y cuidado',
  label: 'Un refugio para volver a vos'
}];
export function MaterialPreview() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const selectPage = useCallback((index: number) => {
    setDirection(index >= activeIndex ? 1 : -1);
    setActiveIndex(index);
  }, [activeIndex]);
  const goPrevious = useCallback(() => {
    setDirection(-1);
    setActiveIndex(current => (current - 1 + previewPages.length) % previewPages.length);
  }, []);
  const goNext = useCallback(() => {
    setDirection(1);
    setActiveIndex(current => (current + 1) % previewPages.length);
  }, []);
  return <Section className="max-w-5xl pt-6 md:pt-12" data-id="element-191">
      <div className="mb-8 text-center md:mb-12" data-id="element-192">
        <div className="mb-5 inline-flex items-center rounded-full border border-brand-pink/25 bg-white/80 px-4 py-1.5 shadow-sm" data-id="element-193">
          <span className="font-serif text-xs font-semibold tracking-wider text-brand-pinkDark md:text-sm" data-id="element-194">
            VISTA PREVIA DEL SANTUARIO
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-brand-pinkDark md:text-5xl" data-id="element-195">
          Conocé el material por dentro
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-text/70 md:text-base" data-id="element-196">
          Páginas amorosamente guiadas para darte contención, palabras y un
          camino posible en los días más difíciles.
        </p>
      </div>

      <div className="mx-auto max-w-4xl" data-id="element-197">
        <div className="relative overflow-hidden rounded-3xl border border-brand-pink/20 bg-white p-3 shadow-[0_18px_50px_rgba(157,23,77,0.12)] sm:p-5" data-id="element-198">
          <div className="relative flex min-h-[420px] items-center justify-center overflow-hidden rounded-2xl bg-brand-surfaceLight/60 px-12 py-6 sm:min-h-[560px] sm:px-16 md:min-h-[640px]" data-id="element-199">
            <AnimatePresence custom={direction} mode="wait" data-id="element-200">
              <motion.img key={previewPages[activeIndex].src} src={previewPages[activeIndex].src} alt={previewPages[activeIndex].alt} custom={direction} initial={{
              opacity: 0,
              x: direction * 36,
              scale: 0.985
            }} animate={{
              opacity: 1,
              x: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              x: direction * -36,
              scale: 0.985
            }} transition={{
              duration: 0.28,
              ease: 'easeOut'
            }} className="max-h-[380px] w-auto max-w-full rounded-lg border border-black/5 object-contain shadow-xl sm:max-h-[520px] md:max-h-[600px]" draggable={false} data-id="element-201" />
            </AnimatePresence>

            <button type="button" onClick={goPrevious} aria-label="Ver página anterior" className="absolute left-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-brand-pink/20 bg-white/95 text-brand-pinkDark shadow-md transition-colors hover:bg-brand-surfaceLight focus:outline-none focus:ring-2 focus:ring-brand-pink focus:ring-offset-2 sm:left-4 sm:h-11 sm:w-11" data-id="element-202">
              <ChevronLeft className="h-5 w-5" aria-hidden="true" data-id="element-203" />
            </button>
            <button type="button" onClick={goNext} aria-label="Ver página siguiente" className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-brand-pink/20 bg-white/95 text-brand-pinkDark shadow-md transition-colors hover:bg-brand-surfaceLight focus:outline-none focus:ring-2 focus:ring-brand-pink focus:ring-offset-2 sm:right-4 sm:h-11 sm:w-11" data-id="element-204">
              <ChevronRight className="h-5 w-5" aria-hidden="true" data-id="element-205" />
            </button>
          </div>

          <div className="px-2 pb-1 pt-5 text-center" data-id="element-206">
            <p className="font-serif text-base font-bold text-brand-pinkDark md:text-lg" data-id="element-207">
              {previewPages[activeIndex].label}
            </p>
            <p className="mt-1 text-xs text-brand-muted" data-id="element-208">
              Página {activeIndex + 1} de {previewPages.length}
            </p>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-center gap-2" aria-label="Seleccionar página de muestra" data-id="element-209">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => selectPage(index)} aria-label={`Ver muestra: ${page.label}`} aria-current={index === activeIndex ? 'true' : undefined} className={`h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-brand-pink focus:ring-offset-2 ${index === activeIndex ? 'w-7 bg-brand-pink' : 'w-2.5 bg-brand-pink/25 hover:bg-brand-pink/55'}`} data-id="element-210" />)}
        </div>
      </div>
    </Section>;
}